/* eslint-disable max-lines */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Header, ImpersonationHeaderProps } from '@im/ui-lib/HeaderX4C';
import { AvatarProps } from '@im/ui-lib/HeaderX4C/internal/Avatar';
import { SearchResponse } from '@im/ui-lib/HeaderX4C/internal/ProductSearchAutocomplete';
import { useCartItemsCount } from '../../hooks/use-cart-items-count';
// import { NotificationDropProps } from '@im/ui-lib/HeaderX4C/internal/NotificationDrop/NotificationDrop';
import { InsightsMessagingQueue } from 'mf_shared/InsightsMessagingQueue';
import { QuickInsights } from 'mf_shared/QuickInsights';
import { HeaderX4CMobile } from '@im/ui-lib/HeaderX4CMobile';
import { useFeatureFlagBase } from '../../hooks/use-feature-flag-base';
import { useLocation, useNavigate } from 'react-router';
import { setDigitalData } from 'mf_core/adobe';
import { useDecodedToken, useHasPermission } from 'mf_core/auth';
import { useIsMutating } from '@tanstack/react-query';
import { useAddToQuote } from 'mf_shared/AddToQuote';
import { useFirebaseLoader } from '../../app/injectors/useFirebase';
import { useMarkAsReadNotifications } from '../../hooks/use-markAsReadForNotifications';
import { FCMEnabler } from './FCMEnabler';
import { useFeatureFlag } from 'mf_core/feature-flag';

type Props = {
  AvatarProps: AvatarProps;
  enableAISuggestions: boolean;
  getSearchSuggestions: (term: string) => Promise<SearchResponse[]>;
  impersonationHeaderProps?: ImpersonationHeaderProps;
  isAuthenticated: boolean;
  isCmpWhitelisted: boolean;
  isLogoClickable?: boolean;
  isMobile: boolean;
  // isNotificationEnabled?: boolean;
  // notificationDropProps: NotificationDropProps;
  onMenuButtonClick: () => void;
  onUserActionClick: (action: UserActionType) => void;
  showAvatar?: boolean;
  showCartIcon?: boolean;
  showCulturePicker?: boolean;
  showFavoritesIcon?: boolean;
  showImpersonationHeader?: boolean;
  showSeachBar?: boolean;
};

type UserActionType = 'favorite' | 'notification' | 'cart' | 'view-quote';

export function IngramHeader(props: Props) {
  const {
    AvatarProps,
    enableAISuggestions,
    getSearchSuggestions,
    impersonationHeaderProps,
    isAuthenticated,
    isCmpWhitelisted,
    isLogoClickable,
    isMobile,
    // isNotificationEnabled,
    // notificationDropProps,
    onMenuButtonClick,
    onUserActionClick,
    showAvatar,
    showCartIcon,
    showCulturePicker,
    showFavoritesIcon,
    showImpersonationHeader,
    showSeachBar,
  } = props;
  const decodedToken = useDecodedToken();
  const { quoteEditorURL, showQuoteButton } = useAddToQuote();
  const { pathname } = useLocation();

  const showQuoteIcon = showCartIcon && showQuoteButton;
  const isCheckout = pathname.startsWith('/my/checkout');
  const isVendorInfo = pathname.startsWith('/my/vendorRequestedInformation');
  const userType: string = decodedToken?.imoProfile?.userType || '';
  const { boValues, isReady: isFlagLoaded } = useFeatureFlagBase();
  const isInsightsMessagingQueue = isFeatureEnabled(
    isFlagLoaded,
    boValues,
    'EnableInsightsMessagingQueue'
  );
  const isQuickInsightsRollDownEnabled = isFeatureEnabled(
    isFlagLoaded,
    boValues,
    'EnableQuickInsightsRollDown'
  );
  const isEndUserFeatureEnabled =
    isFeatureEnabled(isFlagLoaded, boValues, 'X4EUEnabled') &&
    userType === 'EndUser';

  const [isInsightsDropOpen, setIsInsightsDropOpen] = useState(false);
  const [isInsightsButtonClicked, setIsInsightsButtonClicked] = useState(false);
  const [insightEnabledPage, setInsightEnabledPage] = useState('');
  const [hasInsigtData, setHasInsightData] = useState<null | boolean>(null);
  const { isReady, value: boData } = useFeatureFlag([
    'im-customer-usermanagement-notificationsettingzenithview-isenabled',
  ]);

  const showNotificationBellIcon = isReady
    ? boData[
        'im-customer-usermanagement-notificationsettingzenithview-isenabled'
      ] === 'true'
    : false;
  const updateInsightDataStatus = useCallback((hasData: boolean | null) => {
    setHasInsightData(hasData);
  }, []);
  const firebaseReady = useFirebaseLoader();
  const useMarkAsReadNotification = useMarkAsReadNotifications();

  const [chat, setChat] = useState<any>([]);
  const [historyChat, setHistoryChat] = useState<any>([]);
  const latestChat = useRef<any>(null);
  latestChat.current = chat;
  const [fcmToken, setFcmtoken] = useState('');

  const hasPermission = useHasPermission();
  const location = useLocation();
  const enableInsightsPaths = [
    '/insight/insights-v2',
    '/my/cart',
    '/my/dashboard',
    '/product/productsearch',
    '/product/productdetails',
    '/product/subscriptioncategories',
    '/my/ordertracking/v3/ordersearch',
    '/my/ordertracking/v3/orderdetails',
    '/frame',
    '/invoice/invoicessearch',
    '/invoice/invoicessearch',
    '/my/subscriptions/list',
  ];
  const enableRollDownForInsightsPaths = [
    '/my/ordertracking/v3/ordersearch',
    '/my/ordertracking/v3/orderdetails',
    '/frame',
    '/invoice/invoicessearch',
    '/invoice/invoicessearch',
    '/my/subscriptions/list',
  ];
  const navigate = useNavigate();
  const cartItemsCount = useCartItemsCount();
  const [closeInsightsDrop, setCloseInsightsDrop] = useState(false);
  const [unreadMessagesHistoryCount, setUnreadMessagesHistoryCount] =
    useState<any>([]);

  const enableUserManagement = isFeatureEnabled(
    isFlagLoaded,
    boValues,
    'EnableUserManagement'
  );
  const enableInsightsFrameWork = isFeatureEnabled(
    isFlagLoaded,
    boValues,
    'EnableInsightsFrameWork'
  );
  const disableCartIconOnAddToCart = isFeatureEnabled(
    isFlagLoaded,
    boValues,
    'DisableCartIconOnAddToCart'
  );
  const isAddToCartPending =
    useIsMutating({
      mutationKey: ['add-product-to-cart'],
    }) > 0;
  const dismissAllHandler = useCallback(() => {
    setCloseInsightsDrop((prevCloseInsightsDrop) => !prevCloseInsightsDrop);
  }, []);

  const getVisitedPageInfo = useCallback((currentPagePath: string) => {
    const visitedPages = JSON.parse(
      localStorage.getItem('visitedQuickInsightPages') ?? '[]'
    );

    if (visitedPages.includes(currentPagePath ?? ''))
      setCloseInsightsDrop((prevCloseInsightsDrop) => !prevCloseInsightsDrop);
    else setInsightEnabledPage(currentPagePath);
  }, []);

  const onQuickInsightsRollDown = useCallback(() => {
    if (isQuickInsightsRollDownEnabled) {
      setHasInsightData(null);
      if (
        location?.pathname === '/my/ordertracking/v3/ordersearch' ||
        location?.pathname === '/my/ordertracking/v3/orderdetails' ||
        (location?.pathname === '/frame' &&
          location.search.includes('ordersearch'))
      ) {
        getVisitedPageInfo('/my/ordertracking/');
      } else if (
        enableRollDownForInsightsPaths.includes(location?.pathname ?? '')
      ) {
        getVisitedPageInfo(location?.pathname);
      } else if (
        location?.pathname === '/product/productsearch' &&
        location?.search?.includes('vendor') &&
        !location?.search?.includes('vendorname')
      ) {
        getVisitedPageInfo(location?.pathname);
      } else
        setCloseInsightsDrop((prevCloseInsightsDrop) => !prevCloseInsightsDrop);
    } else
      setCloseInsightsDrop((prevCloseInsightsDrop) => !prevCloseInsightsDrop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getVisitedPageInfo, isQuickInsightsRollDownEnabled, location]);

  const onClickInsightsCenter = useCallback(
    (navigateLink = '/insight/insights-v2') => {
      navigate(navigateLink);
      onQuickInsightsRollDown();
    },
    [navigate, onQuickInsightsRollDown]
  );

  const onHeaderUserAction = (action: UserActionType) => {
    switch (action) {
      case 'view-quote':
        navigate(quoteEditorURL);
        break;
      default:
        onUserActionClick(action);
        break;
    }
  };

  const flag = JSON.parse(localStorage.getItem('setValueFlag') || 'false');
  setTimeout(() => {
    if (flag) {
      const valueGet = JSON.parse(localStorage.getItem('isEnabledOrNot') || '');
      const QuoteCheckoutBtnStatus = valueGet ? 'Enabled' : 'Disabled';
      setDigitalData({ QuoteCheckoutBtnStatus });
    }
  }, 1000);

  useEffect(() => {
    onQuickInsightsRollDown();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.pathname]);

  useEffect(() => {
    const historyChatUnReadmessages = historyChat.filter(
      (item: any) => item?.isRead === false
    );
    setUnreadMessagesHistoryCount(historyChatUnReadmessages?.length);
    const result: any[] = [];
    historyChat.forEach((item: any, index: number, self: any) => {
      if (
        !(
          index ===
          self.findIndex(
            (data: any) => data.notificationId === item.notificationId
          )
        )
      )
        if (item.isRead === false) {
          result.push(item);
        }
    });
    setUnreadMessagesHistoryCount(result?.length);
  }, [chat, historyChat]);
  const listener = useCallback((event: MessageEvent) => {
    const updatedChat = [...latestChat.current];
    const isduplicate = updatedChat.some((item) => {
      return (
        (item.messageId !== undefined &&
          item.messageId === event.data.messageId) ||
        (item.messageId !== undefined &&
          item.messageId === event.data.fcmMessageId) ||
        (item.title !== undefined && item.title === event.data.data.title) ||
        (item.content !== undefined && item.content === event.data.data.content)
      );
    });
    if (event?.data && !isduplicate) {
      updatedChat.push(event.data);

      const Chat = updatedChat
        .filter((item) => item?.data?.title || item?.data?.content)
        .map((item) => ({
          ...item,
          content: item?.data?.content,
          createdDate: item?.createdDate || new Date().toISOString(),
          title: item?.data?.title,
        }));

      if (Chat.length > 0) {
        setChat(Chat);
      }
    }

    return () => {
      navigator?.serviceWorker?.removeEventListener('message', listener);
    };
  }, []);
  navigator?.serviceWorker?.addEventListener('message', listener);

  useEffect(() => {
    if (isInsightsButtonClicked) {
      let dataStatus: boolean | null = true;

      if (hasInsigtData === null) dataStatus = false;
      else dataStatus = hasInsigtData;

      setHasInsightData(dataStatus);
    }
  }, [hasInsigtData, isInsightsButtonClicked]);
  const markAllAsRead = async () => {
    const notificationIds = [...chat, ...historyChat]
      .map((item) => item?.data?.notificationId || item?.notificationId) // ✅ Handle both structures
      .filter(Boolean); // ✅ Remove undefined values
    setChat([]);
    useMarkAsReadNotification.mutate({
      notificationIds,
    });
  };
  const getNotificationId = (item: any) => {
    return item?.data?.notificationId || item?.notificationId;
  };
  const markAsReadItem = (item: any) => {
    setChat([]);
    const notificationId = getNotificationId(item);
    useMarkAsReadNotification.mutate({
      notificationIds: [notificationId, 'read'],
    });
  };
  const dismiss = (item: any) => {
    setChat([]);
    const notificationId = getNotificationId(item);
    useMarkAsReadNotification.mutate({
      notificationIds: [notificationId, 'delete'],
    });
  };
  const onNotificationItemClick = (item: any) => {
    setChat([]);
    const notificationId = getNotificationId(item);
    useMarkAsReadNotification.mutate({
      notificationIds: [notificationId, 'read'],
    });
  };

  const removeDuplicate = (notifications: any[]) => {
    const result: any[] = [];

    notifications.forEach((item, index, self) => {
      const id = getNotificationId(item);
      if (id == null) return;

      const isFirst =
        index ===
        self.findIndex((data) => {
          const indexId = getNotificationId(data);
          return indexId === id;
        });

      if (isFirst) {
        result.push(item);
      }
    });

    result.sort((a, b) => {
      const dateA = new Date(a.createdDate).getTime();
      const dateB = new Date(b.createdDate).getTime();
      return dateB - dateA;
    });

    return result.map((item) => {
      const content = item.content ?? '';
      const anchorTagRegex = /<a\s+href="(.*?)">(.*?)<\/a>/g;

      const elements: (string | JSX.Element)[] = [];
      let lastIndex = 0;
      let match;

      while ((match = anchorTagRegex.exec(content)) !== null) {
        if (match.index > lastIndex) {
          elements.push(content.substring(lastIndex, match.index));
        }

        const [fullMatch, href, text] = match;
        elements.push(
          <a
            href={href}
            key={`${href}-${match.index}`}
            rel="noopener noreferrer"
            style={{ color: '#1976d2', textDecoration: 'underline' }}
            target="_blank"
          >
            {text}
          </a>
        );

        lastIndex = match.index + fullMatch.length;
      }

      if (lastIndex < content.length) {
        elements.push(content.substring(lastIndex));
      }
      /* eslint-disable sort-keys */
      return {
        ...item,
        content: <span>{elements}</span>,
        action: undefined,
      };
    });
  };

  return (
    <>
      {isReady && showNotificationBellIcon && (
        <FCMEnabler
          chat={chat}
          fcmToken={fcmToken}
          firebaseReady={firebaseReady}
          historyChat={historyChat}
          latestChat={latestChat}
          setChat={setChat}
          setFcmtoken={setFcmtoken}
          setHistoryChat={setHistoryChat}
        />
      )}
      {!isAuthenticated && isMobile ? (
        <HeaderX4CMobile
          enableAISuggestions={false}
          getSearchSuggestions={getSearchSuggestions}
          isLogoClickable={isLogoClickable}
          onMenuButtonClick={onMenuButtonClick}
        />
      ) : (
        <Header
          AvatarProps={AvatarProps}
          NotificationProps={{
            dismiss: (item) => {
              dismiss(item);
            },

            markAllAsRead: () => {
              markAllAsRead();
            },
            markAsReadItem: (item: any) => {
              markAsReadItem(item);
            },
            notifications: removeDuplicate([...chat, ...historyChat]),
            onNotificationItemClick: (item: any) => {
              onNotificationItemClick(item);
            },
            openNotifications: function noRefCheck() {},
            unReadCount: unreadMessagesHistoryCount + chat?.length,
          }}
          cartItemsCount={cartItemsCount}
          closeInsightsDrop={closeInsightsDrop}
          data-testid="header"
          disableCartIcon={disableCartIconOnAddToCart && isAddToCartPending}
          enableAISuggestions={enableAISuggestions}
          endUserIcon={isEndUserFeatureEnabled ? EndUserIcon : undefined}
          endUserTitle={isEndUserFeatureEnabled ? 'DEMO RESELLER' : undefined}
          getSearchSuggestions={getSearchSuggestions}
          hasInsightData={hasInsigtData}
          impersonationHeaderProps={impersonationHeaderProps}
          insightsContent={
            enableInsightsFrameWork ? (
              <QuickInsights
                hasInsightsData={hasInsigtData}
                isInsightsButtonClicked={isInsightsButtonClicked}
                isInsightsDropOpen={isInsightsDropOpen}
                onClickInsightsCenter={onClickInsightsCenter}
                updateInsightDataStatus={updateInsightDataStatus}
              />
            ) : (
              <InsightsMessagingQueue
                dismissAllHandler={dismissAllHandler}
                maxDropHeight={260}
                onClickInsightsCenter={onClickInsightsCenter}
              />
            )
          }
          isAuthenticated={isAuthenticated}
          isCmpWhitelisted={enableAISuggestions || isCmpWhitelisted}
          isEndUserLogin={isEndUserFeatureEnabled}
          isLogoClickable={isLogoClickable}
          onMenuButtonClick={onMenuButtonClick}
          onUserActionClick={onHeaderUserAction}
          page={insightEnabledPage}
          setIsInsightsButtonClicked={setIsInsightsButtonClicked}
          setIsInsightsDropOpen={setIsInsightsDropOpen}
          showAvatar={showAvatar}
          showCartIcon={showCartIcon && !showQuoteIcon}
          showCulturePicker={showCulturePicker}
          showFavoritesIcon={showFavoritesIcon}
          showImpersonationHeader={showImpersonationHeader}
          showInsightsIcon={
            isInsightsMessagingQueue &&
            enableInsightsPaths.includes(location?.pathname ?? '') &&
            hasPermission(
              enableUserManagement ? ['ViewInsight'] : ['ResellerAdmin']
            )
          }
          // showNotificationIcon={isNotificationEnabled}
          showNotificationIcon={
            showNotificationBellIcon && !isCheckout && !isVendorInfo
          }
          showQuoteIcon={showQuoteIcon}
          showSeachBar={showSeachBar}
        />
      )}
    </>
  );
}

const EndUserIcon: JSX.Element = (
  <img
    alt="DEMO RESELLER"
    src="/cep/assets/img/enduser-poc/demo-header-icon.svg"
    width={70}
  />
);
function isFeatureEnabled(
  isFlagLoaded: boolean,
  boValues: any,
  featureName: string
) {
  return isFlagLoaded && boValues[featureName] === 'true';
}
